import FormControl from '@ui-components-3/ui/lib/components/FormControl'
import Modal from '@ui-components-3/ui/lib/components/Modal'
import Select from '@ui-components-3/ui/lib/components/Select'
import { Observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import AudioVisualizer from '../AudioVisualizer'
import DeviceSettings from 'models/DeviceSettings'
import Switch from '@material-ui/core/Switch'
import Loading from '@ui-components-3/ui/lib/components/Loading'
import User from 'singletons/User'
import Notice from '@ui-components-3/ui/lib/components/Notice'

type CallDeviceSettingsModalProps = {
  deviceSettings: DeviceSettings
  opened: boolean
  onClose: () => void
}

const CallDeviceSettingsModal = ({ deviceSettings, opened, onClose }: CallDeviceSettingsModalProps) => {
  const videoRef = useRef(null)

  const { type } = deviceSettings?.callInstance || {}

  const isPhoneCall = type === 'phone'

  useEffect(() => {
    deviceSettings.initializeDevices(videoRef)
    return () => {
      deviceSettings.reset()
    }
  }, [])

  return (
    <Modal
      onClose={() => {
        deviceSettings.reset()
        onClose()
      }}
      label={isPhoneCall ? 'Phone Call Device Settings' : 'Video Chat Settings'}
      opened={opened}
      footer={({ close }) => (
        <>
          <button onClick={close} className="btn btn-neutral-borderless">
            Cancel
          </button>
          <button
            onClick={async () => {
              await deviceSettings.saveChanges()
              onClose()
            }}
            className="btn btn-primary-outlined"
          >
            Save
          </button>
        </>
      )}
    >
      <div>
        <div className="gap-s flex flex-col">
          <Observer>
            {() => (
              <>
                {!isPhoneCall && (
                  <>
                    <p className="typography-body-l">Camera Preview</p>
                    <video
                      autoPlay
                      ref={videoRef}
                      className={'mt-1 mb-3 aspect-video w-[350px] object-cover overflow-hidden self-center'}
                      style={{
                        transform: 'scale(-1, 1)',
                      }}
                    />

                    <div className="flex flex-col items-stretch self-stretch gap-3 mb-5">
                      {!User.isVirtualBackgroundEnabled ? (
                        <Notice variant="warning">
                          The TimelyCare video background is currently disabled for maintenance.
                        </Notice>
                      ) : null}
                      <label className="self-end flex items-center justify-end typography-body-l text-neutral-800 font-medium">
                        <span>TimelyCare Background</span>
                        <Switch
                          checked={User.isVirtualBackgroundEnabled && !!deviceSettings.hasSelectedVideoBackground}
                          onChange={(event) => deviceSettings.setSelectedVideoBackground(!!event.target.checked)}
                          name="hasBackground"
                          inputProps={{ 'aria-label': 'TimelyCare Background' }}
                          disabled={!User.isVirtualBackgroundEnabled}
                        />
                        <span className="w-6 text-right">
                          {deviceSettings.hasSelectedVideoBackground ? 'On' : 'Off'}
                        </span>
                      </label>
                      <p className="typography-small text-neutral-800 italic">
                        The TimelyCare video background is not reflected in the camera preview and will be displayed
                        within the video session.
                      </p>
                    </div>
                    {!!deviceSettings.isCamerasReady && (
                      <FormControl label="Change Camera">
                        <Select
                          options={deviceSettings.availableCameras}
                          value={deviceSettings.selectedCameraId}
                          getOptionLabel={(u) => u.label}
                          getOptionValue={(u) => u.deviceId}
                          onChange={(value) => deviceSettings.changeCameraPreview(value)}
                        />
                      </FormControl>
                    )}
                  </>
                )}
                {!!deviceSettings.isSpeakersReady && (
                  <FormControl label="Change Speaker">
                    <Select
                      options={deviceSettings.availableSpeakers}
                      value={deviceSettings.selectedSpeakerId}
                      getOptionLabel={(o) => o.label}
                      getOptionValue={(o) => o.deviceId}
                      onChange={(value) => deviceSettings.changeSpeakerPreview(value)}
                    />
                  </FormControl>
                )}
                {deviceSettings.isMicrophonesReady ? (
                  <>
                    <FormControl label="Change Microphone">
                      <Select
                        options={deviceSettings.availableMicrophones}
                        value={deviceSettings.selectedMicrophoneId}
                        getOptionLabel={(o) => o.label}
                        getOptionValue={(o) => o.deviceId}
                        onChange={(value) => deviceSettings.changeMicrophonePreview(value)}
                      />
                    </FormControl>
                    <AudioVisualizer audioLevel={deviceSettings.microphoneVisualizer.lastPolledAudioLevel} />
                  </>
                ) : (
                  <Loading shown />
                )}
              </>
            )}
          </Observer>
        </div>
      </div>
    </Modal>
  )
}

export default CallDeviceSettingsModal
